import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { CheckCircleOutlined, CancelOutlined, AddOutlined } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';

import { useMountEffect, useRequest, useTableState, usePerformActionOnSuccess } from 'shared/hooks';
import api from 'api';
import { getTableHeaders, prepareTableData } from 'utils/tables';
import { formatDateToStr } from 'utils/dates';
import { isProdEnvironment } from 'utils/constants';
import colors from 'styles/colors';
import { SuccessActionTypesEnum } from 'store/SuccessActionTypes.enum';
import { adminActions } from 'features/admin/store/slice';

import PaginatedTable from 'shared/components/data/PaginatedTable/PaginatedTable';
import LoadingSpinner from 'shared/components/feedback/LoadingSpinner/LoadingSpinner';
import ActionButton from 'shared/components/buttons/ActionButton/ActionButton';
import { FlexBoxColumn, FlexBoxSpaceBetween, Box } from 'styles/layout';
import Hl7OrderForm from 'features/admin/components/Hl7OrderForm';
import { mapApi2Form } from 'features/admin/components/Hl7OrderForm/utils';

const tableColumns = [
  {
    header: {
      id: 'givenName',
      value: 'Given Name',
    },
    dataKey: 'givenName',
    width: '10%',
  },
  {
    header: {
      id: 'familyName',
      value: 'Family Name',
    },
    dataKey: 'familyName',
    width: '10%',
  },
  {
    header: {
      id: 'dob',
      value: 'Date of Birth',
    },
    formatValue: ({ dob }) => (dob ? formatDateToStr(dob) : '-'),
    width: '10%',
  },
  {
    header: {
      id: 'patientAccountNumber',
      value: 'Patient Account Number',
    },
    dataKey: 'patientAccountNumber',
    width: '15%',
  },
  {
    header: {
      id: 'testCodeId',
      value: 'Test Code ID',
    },
    dataKey: 'testCodeId',
    width: '12%',
  },
  {
    header: {
      id: 'testCodeText',
      value: 'Test Code Text',
    },
    dataKey: 'testCodeText',
  },
  {
    header: {
      id: 'isMatched',
      value: 'Matched',
    },
    width: '100px',
    align: 'center',
    formatValue: ({ isMatched }) =>
      isMatched ? (
        <CheckCircleOutlined fontSize="small" sx={{ color: colors.success }} />
      ) : (
        <CancelOutlined fontSize="small" sx={{ color: colors.notification }} />
      ),
  },
];

const Hl7OrdersListTab = ({ hl7PatientId }) => {
  const dispatch = useDispatch();
  const { data: ordersData, doRequest } = useRequest(api.getDetailedOrdersList);
  const { page, handlePageChange } = useTableState();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedOrder, setSelectedOrder] = React.useState(null);

  const isEditMode = Boolean(selectedOrder);

  const fetchOrders = React.useCallback(() => {
    if (!hl7PatientId) return;
    doRequest({ page, hl7PatientId });
  }, [doRequest, page, hl7PatientId]);

  useMountEffect(fetchOrders);

  React.useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  usePerformActionOnSuccess(SuccessActionTypesEnum.HL7_ORDER_CREATED, () => {
    setModalOpen(false);
    setSelectedOrder(null);
    fetchOrders();
  });

  usePerformActionOnSuccess(SuccessActionTypesEnum.HL7_ORDER_UPDATED, () => {
    setModalOpen(false);
    setSelectedOrder(null);
    fetchOrders();
  });

  const handleAddNew = React.useCallback(() => {
    setSelectedOrder(null);
    setModalOpen(true);
  }, []);

  const handleRowClick = React.useCallback((rowData) => {
    const clickedId = typeof rowData === 'object' ? rowData?.id : rowData;
    const matchedOrder = ordersData?.data?.find(
      ({ id }) => String(id) === String(clickedId),
    );

    if (!matchedOrder) return;

    setSelectedOrder(matchedOrder);
    setModalOpen(true);
  }, [ordersData?.data]);

  const handleClose = React.useCallback(() => {
    setModalOpen(false);
    setSelectedOrder(null);
  }, []);

  const handleSubmit = React.useCallback(
    (values) => {
      if (isEditMode) {
        dispatch(adminActions.updateHl7Order({ id: selectedOrder.id, ...values }));
      } else {
        dispatch(
          adminActions.createHl7Order({
            hl7PatientId: parseInt(hl7PatientId, 10),
            ...values,
          }),
        );
      }
    },
    [isEditMode, selectedOrder, hl7PatientId, dispatch],
  );

  const initValues = React.useMemo(
    () => (selectedOrder ? mapApi2Form(selectedOrder) : {}),
    [selectedOrder],
  );

  if (!ordersData) return <LoadingSpinner />;

  return (
    <FlexBoxColumn p={2}>
      {!isProdEnvironment && (
        <FlexBoxSpaceBetween mb={2}>
          <Box />
          <ActionButton startIcon={<AddOutlined />} onClick={handleAddNew}>
            Add New
          </ActionButton>
        </FlexBoxSpaceBetween>
      )}

      <PaginatedTable
        headings={getTableHeaders(tableColumns)}
        data={prepareTableData(ordersData?.data || [], tableColumns)}
        fallbackMsg="No orders found for this patient"
        currentPage={page}
        totalPages={ordersData?.pageCount}
        onPageChange={handlePageChange}
        onClickRow={!isProdEnvironment ? handleRowClick : undefined}
      />

      {!isProdEnvironment && (
        <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>{isEditMode ? 'Edit HL7 Order' : 'Add HL7 Order'}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Hl7OrderForm
              initValues={initValues}
              onSubmit={handleSubmit}
              onCancel={handleClose}
              hideButtons
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary" variant="outlined">
              Cancel
            </Button>
            <Button form="hl7OrderForm" type="submit" color="primary" variant="contained">
              {isEditMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </FlexBoxColumn>
  );
};

Hl7OrdersListTab.propTypes = {
  hl7PatientId: PropTypes.string.isRequired,
};

export default Hl7OrdersListTab;
